import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { CRE_EXTENSION_PATH, CRE_LOGIN_PATH } from "../constants/routes";
import {
  MedMicAuthCoordinator,
} from "./medmicAuthCoordinator";
import { useMedMicAuthStore } from "./medmicAuthStore";
import { setCreLoginIntent } from "./creLoginIntent";
import { setMedmicReturnRoute } from "./creMedmicReturnRoute";
import { creLoginRequest } from "./creAuthConfig";
import {
  consumeMedMicExplicitConnect,
  markMedMicExplicitConnect,
  markMedMicPreferActiveAccount,
} from "./medmicExplicitConnect";

export default function CreExtensionHandoffPage() {
  const medmic = useMedMicAuthStore();
  const [micPermission, setMicPermission] = useState("unknown");
  const [micStatus, setMicStatus] = useState("");
  const [initDone, setInitDone] = useState(false);
  const micPermissionStatusRef = useRef(null);
  const { instance } = useMsal();

  const refreshMicPermission = async () => {
    if (micPermissionStatusRef.current) {
      micPermissionStatusRef.current.onchange = null;
      micPermissionStatusRef.current = null;
    }
    try {
      const result = await navigator.permissions.query({
        name: "microphone",
      });
      micPermissionStatusRef.current = result;
      setMicPermission(result.state);
      result.onchange = () => setMicPermission(result.state);
    } catch {
      setMicPermission("unknown");
    }
  };

  const handleGiveMicPermission = async () => {
    setMicStatus("Requesting microphone permission...");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      setMicPermission("granted");
      setMicStatus("Microphone permission granted.");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Microphone permission denied.";
      setMicStatus(`Could not grant microphone permission: ${message}`);
      void refreshMicPermission();
    }
  };

  useEffect(() => {
    MedMicAuthCoordinator.startBridge();
    void refreshMicPermission();

    let cancelled = false;
    let timer = 0;

    (async () => {
      await MedMicAuthCoordinator.initialize();
      const explicitConnect = consumeMedMicExplicitConnect();

      if (MedMicAuthCoordinator.isDisconnected() && !explicitConnect) {
        MedMicAuthCoordinator.publishCurrentSession({ broadcast: false });
        if (!cancelled) setInitDone(true);
        return;
      }

      if (explicitConnect) {
        await MedMicAuthCoordinator.connect({
          source: "cre_extension_handoff_explicit",
        });
      } else {
        const snap = MedMicAuthCoordinator.getSnapshot();
        if (snap.state === "authenticated" && snap.accessToken) {
          MedMicAuthCoordinator.publishCurrentSession({ broadcast: true });
        } else {
          await MedMicAuthCoordinator.bootstrap({
            source: "cre_extension_handoff",
          });
        }
      }
      if (cancelled) return;
      setInitDone(true);
      MedMicAuthCoordinator.publishCurrentSession({ broadcast: false });
      timer = window.setTimeout(() => {
        MedMicAuthCoordinator.clearHandoffStorage();
      }, 30_000);
    })();

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      if (micPermissionStatusRef.current) {
        micPermissionStatusRef.current.onchange = null;
      }
    };
  }, []);

  const isBusy =
    !initDone ||
    medmic.state === "authenticating" ||
    medmic.state === "validating" ||
    medmic.state === "disconnecting" ||
    medmic.state === "refreshing";

  if (isBusy) {
    return (
      <div className="cre-root cre-loading">
        <div className="cre-spinner" />
        <span>
          {medmic.state === "disconnecting"
            ? "Disconnecting MedMic…"
            : medmic.state === "refreshing"
              ? "Refreshing MedMic session…"
              : "Connecting MedMic…"}
        </span>
      </div>
    );
  }

  if (
    medmic.disconnected ||
    medmic.state === "disconnected" ||
    medmic.state === "signed_out" ||
    medmic.state === "validation_failed" ||
    medmic.state === "interaction_required" ||
    medmic.state === "expired" ||
    medmic.state === "refresh_failed"
  ) {
    const reconnectTitle =
      medmic.state === "validation_failed" ||
      medmic.state === "interaction_required" ||
      medmic.state === "expired" ||
      medmic.state === "refresh_failed"
        ? "Your MedMic session has expired"
        : "MedMic disconnected";
    const reconnectMessage =
      medmic.state === "validation_failed" ||
      medmic.state === "interaction_required" ||
      medmic.state === "expired" ||
      medmic.state === "refresh_failed"
        ? medmic.error?.message ||
          "Connect MedMic again to continue. Enterprise CRE is not affected."
        : "Connect MedMic to use the extension. Enterprise CRE is not affected.";
    return (
      <div className="cre-root cre-session-page">
        <div className="cre-login-bg" aria-hidden />
        <div className="cre-session-card">
          <h2 className="cre-session-error-title">{reconnectTitle}</h2>
          <p className="cre-session-error-message">{reconnectMessage}</p>
          <Link
            to={CRE_EXTENSION_PATH}
            className="cre-btn-signin"
            style={{ display: "inline-block", textDecoration: "none" }}
          >
            Connect MedMic
          </Link>
        </div>
      </div>
    );
  }

  if (medmic.state !== "authenticated") {
    return (
      <div className="cre-root cre-session-page">
        <div className="cre-login-bg" aria-hidden />
        <div className="cre-session-card">
          <h2 className="cre-session-error-title">Session unavailable</h2>
          <p className="cre-session-error-message">
            Please connect MedMic again.
          </p>
          <Link
            to={CRE_EXTENSION_PATH}
            className="cre-btn-signin"
            style={{ display: "inline-block", textDecoration: "none" }}
          >
            Connect MedMic
          </Link>
        </div>
      </div>
    );
  }

  const userEmail = medmic.userEmail || "";

  return (
    <div className="cre-root cre-session-page">
      <div className="cre-login-bg" aria-hidden />
      <div className="cre-session-card" style={{ textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
        <h2 style={{ margin: "0 0 8px", fontSize: 18 }}>You&apos;re signed in</h2>
        {userEmail && (
          <p
            style={{
              color: "var(--cre-muted, #9ca3af)",
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            {userEmail}
          </p>
        )}
        {medmic.refreshWarning && (
          <p
            style={{
              color: "var(--cre-muted, #9ca3af)",
              fontSize: 12,
              marginBottom: 12,
            }}
          >
            Session refresh is delayed. MedMic is still connected.
          </p>
        )}
        <p
          style={{
            color: "var(--cre-muted, #9ca3af)",
            fontSize: 13,
            lineHeight: 1.5,
            marginBottom: 16,
          }}
        >
          Your session was passed to the extension. Keep this tab{" "}
          <strong>pinned</strong>, then open the floating panel.
        </p>
        <button
          type="button"
          id="cre-open-pip-btn"
          className="cre-btn-signin"
          style={{ width: "100%", marginBottom: 12, cursor: "pointer" }}
        >
          Open floating panel
        </button>
        {micPermission !== "granted" && (
          <>
            <p
              style={{
                color: "var(--cre-muted, #9ca3af)",
                fontSize: 12,
                lineHeight: 1.5,
                marginBottom: 8,
              }}
            >
              If dictation microphone does not work, give browser microphone
              permission.
            </p>
            <button
              type="button"
              className="cre-btn-signin"
              style={{
                width: "100%",
                marginBottom: 12,
                cursor: "pointer",
                background: "transparent",
                border: "1px solid var(--cre-border, #2f3748)",
              }}
              onClick={() => void handleGiveMicPermission()}
            >
              Give microphone permission
            </button>
            {micStatus && (
              <p
                style={{
                  color: "var(--cre-muted, #9ca3af)",
                  fontSize: 12,
                  lineHeight: 1.5,
                  marginBottom: 8,
                }}
              >
                {micStatus}
              </p>
            )}
          </>
        )}
        <p
          style={{
            color: "var(--cre-muted, #9ca3af)",
            fontSize: 12,
            lineHeight: 1.5,
            marginBottom: 8,
          }}
        >
          If floating window does not open, please{" "}
          <button
            type="button"
            className="cre-btn-text-inline"
            onClick={() => window.location.reload()}
          >
            reload this page
          </button>
          .
        </p>
        <button
          type="button"
          className="cre-btn-signin"
          style={{
            width: "100%",
            marginBottom: 12,
            cursor: "pointer",
            background: "transparent",
            border: "1px solid var(--cre-border, #2f3748)",
          }}
          onClick={() => {
            MedMicAuthCoordinator.disconnect("handoff");
          }}
        >
          Disconnect MedMic
        </button>
        <button
          type="button"
          className="cre-btn-signin"
          style={{
            width: "100%",
            marginBottom: 12,
            cursor: "pointer",
            background: "transparent",
            border: "1px solid var(--cre-border, #2f3748)",
          }}
          onClick={() => {
            MedMicAuthCoordinator.disconnect("switch_account");
            setCreLoginIntent("extension");
            setMedmicReturnRoute(`${CRE_EXTENSION_PATH}/handoff`);
            markMedMicExplicitConnect();
            markMedMicPreferActiveAccount();
            instance.loginRedirect(creLoginRequest).catch(() => {});
          }}
        >
          Use another Microsoft account
        </button>
        <p style={{ fontSize: 12, color: "var(--cre-muted, #9ca3af)" }}>
          Enterprise hospital user?{" "}
          <Link to={CRE_LOGIN_PATH} style={{ color: "var(--cre-accent)" }}>
            Sign in to CRE workspace
          </Link>
        </p>
      </div>
    </div>
  );
}
